import { useCallback, useEffect } from 'react';
import { showSnackbar } from '../store/slices/uiSlice';
import { store } from '../store';
import type { ApiError } from './api';
import { extractErrorCode, formatErrorCode } from './errorCodes';

interface AxiosErrorWithResponse {
	response?: {
		data?: ApiError;
		status?: number;
	};
	message?: string;
}

export function useErrorHandler() {
	const showError = useCallback((error: unknown, defaultMessage = 'Có lỗi không xác định xảy ra') => {
		let message = defaultMessage;

		// Handle Axios errors with API response
		if (error && typeof error === 'object' && 'response' in error) {
			const axiosError = error as AxiosErrorWithResponse;
			if (axiosError.response?.data) {
				const apiError = axiosError.response.data;

				// Show main error message
				message = apiError.message || defaultMessage;

				// If there are validation errors, show them
				if (apiError.errors && Array.isArray(apiError.errors)) {
					const validationMessages = apiError.errors.map(err =>
						`${err.field}: ${err.message}`
					).join(', ');
					message = `${message}. Chi tiết: ${validationMessages}`;
				}
			} else if (axiosError.response?.status) {
				message = `Lỗi HTTP ${axiosError.response.status}: ${axiosError.message || defaultMessage}`;
			}
		}
		// Handle regular Error objects
		else if (error instanceof Error) {
			message = error.message;
		}
		// Handle string errors
		else if (typeof error === 'string') {
			message = error;
		}
		// Handle objects with message property
		else if (error && typeof error === 'object' && 'message' in error) {
			message = String(error.message);
		}

		// Filter out Zalo-related error notifications
		if (message.toLowerCase().includes('zalo')) {
			console.warn('Zalo error suppressed:', message);
			return;
		}

		const code = extractErrorCode(error);
		store.dispatch(showSnackbar({
			message: `${message} ${formatErrorCode(code)}`,
			severity: 'error'
		}));
	}, []);

	const showSuccess = useCallback((message: string) => {
		store.dispatch(showSnackbar({
			message,
			severity: 'success'
		}));
	}, []);

	const showWarning = useCallback((message: string) => {
		store.dispatch(showSnackbar({
			message,
			severity: 'warning'
		}));
	}, []);

	const showInfo = useCallback((message: string) => {
		store.dispatch(showSnackbar({
			message,
			severity: 'info'
		}));
	}, []);

	return {
		showError,
		showSuccess,
		showWarning,
		showInfo
	};
}

// Hook to handle global unhandled errors
export function useGlobalErrorHandler() {
	const { showError } = useErrorHandler();

	useEffect(() => {
		const handleError = (event: ErrorEvent) => {
			console.error('Global error:', event.error);
			showError(event.error, 'Có lỗi không mong muốn xảy ra');
		};

		const handlePromiseRejection = (event: PromiseRejectionEvent) => {
			console.error('Unhandled promise rejection:', event.reason);
			showError(event.reason, 'Có lỗi không mong muốn xảy ra');
		};

		window.addEventListener('error', handleError);
		window.addEventListener('unhandledrejection', handlePromiseRejection);

		return () => {
			window.removeEventListener('error', handleError);
			window.removeEventListener('unhandledrejection', handlePromiseRejection);
		};
	}, [showError]);
}